import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    reactiveWorkspaceActions,
    setReactiveWorkspaceActions,
} from 'api/graphQL/reactiveVars/workspaceActions'
import { ClassicEditor } from 'ckeditor5'
import { sanitizeAndExtractText } from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import { TextElement } from 'components/WorkspaceEditor/services/TextElementParser'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { getBaseElementId } from 'components/WorkspaceEditor/utils'
import { getContentItemViewNodeByElementId } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react'
import { v4 as newGuid } from 'uuid'
import { useWorkspace2Store } from '../../../Workspace2Store'
import {
    buildNodesWithInsertedUOM,
    buildTextElBase,
} from '../utils/uomCursorHelpers'
import { useInsertUOMAtCursor } from './useInsertUOMAtCursor'

interface UsePlainTextUOMInsertParams {
    editorRef: MutableRefObject<ClassicEditor | undefined>
    skipNextBlurRef: MutableRefObject<boolean>
    textElement: TextContentElement
    setUnsavedData: React.Dispatch<React.SetStateAction<string>>
    updateTreeViewNodes: (nodes: Partial<ContentItemViewNodesType>[]) => void
    debouncedOnChange: { cancel: () => void }
    deactivateCKEditor: () => void
    processEditorContent: (html: string) => TextElement[]
    scheduleInsertAfterSave: (
        postSave: () => void,
        postGet: (
            serverNodes: Partial<ContentItemViewNodesType>[],
            baseNodes: Partial<ContentItemViewNodesType>[]
        ) => Partial<ContentItemViewNodesType>[],
        uomId: string
    ) => void
    handleAddContentExecuteOptimisticWithFallback: (
        unsavedData: string,
        textElement: TextContentElement,
        blurStartActiveTab?: number
    ) => Promise<void>
    isCurrentlyOnOMWorkspace: boolean
    nodeType?: string
    isParagraphUnsaved: boolean
    onInsertUOMRef: MutableRefObject<(() => void) | undefined>
    ckeToggleActive: boolean
}

export const usePlainTextUOMInsert = ({
    editorRef,
    skipNextBlurRef,
    textElement,
    setUnsavedData,
    updateTreeViewNodes,
    debouncedOnChange,
    deactivateCKEditor,
    processEditorContent,
    scheduleInsertAfterSave,
    handleAddContentExecuteOptimisticWithFallback,
    isCurrentlyOnOMWorkspace,
    nodeType,
    isParagraphUnsaved,
    onInsertUOMRef,
    ckeToggleActive,
}: UsePlainTextUOMInsertParams): void => {
    const canEnableInsertDirectUOM =
        isCurrentlyOnOMWorkspace && nodeType !== 'Article'

    const insertUOMAtCursor = useInsertUOMAtCursor({
        editorRef,
        skipNextBlurRef,
        textElement,
        setUnsavedData,
        updateTreeViewNodes,
        debouncedOnChange,
        deactivateCKEditor,
        processEditorContent,
    })

    const isParagraphUnsavedRef = useRef(isParagraphUnsaved)
    const insertUOMAtCursorRef = useRef(insertUOMAtCursor)
    useLayoutEffect(() => {
        isParagraphUnsavedRef.current = isParagraphUnsaved
        insertUOMAtCursorRef.current = insertUOMAtCursor
    })

    const insertUOMAtCursorWithSaveFirst = useCallback(() => {
        if (isParagraphUnsavedRef.current) {
            const editor = editorRef.current
            if (!editor) return

            // Capture editor state now. The paragraph save causes the component to
            // unmount+remount (setTextContentElementToAdd(null) removes the optimistic
            // node, then the GET re-adds it). editorRef will be null by the time the
            // scheduled callback fires, so we must not rely on it there.
            const html = editor.getData()
            const offset =
                editor.model.document.selection.getFirstPosition()?.offset ?? 0
            const annotated = processEditorContent(html)
            // sanitizeAndExtractText strips both CKEditor suggestion markers and
            // deletion suggestion pairs (removeDeletionMarkers), matching the path
            // taken by debouncedOnChange (getUnresolvedChanges → sanitizeAndExtractText).
            const currentContent = sanitizeAndExtractText(html)

            // offset is in CKEditor model space, which includes DELETED suggestion
            // characters. sanitizeAndExtractText removes those before saving, so the
            // server stores text without them. Compute cleanOffset (model offset
            // excluding DELETED chars) and cleanAnnotated (DELETED segments removed)
            // so buildNodesWithInsertedUOM splits at the correct position in saved text.
            const cleanAnnotated = annotated.filter(
                (el) => el.changeType !== 'DELETED'
            )
            let modelCharsWalked = 0
            let cleanOffset = 0
            for (const el of annotated) {
                if (modelCharsWalked >= offset) break
                const text = el.text ?? ''
                const charsConsumed = Math.min(
                    text.length,
                    offset - modelCharsWalked
                )
                if (el.changeType !== 'DELETED') cleanOffset += charsConsumed
                modelCharsWalked += charsConsumed
            }

            debouncedOnChange.cancel()
            skipNextBlurRef.current = true
            deactivateCKEditor()

            // Generate once — shared between postSave and postGet callbacks.
            const newUOMId = newGuid()

            const textEl = textElement as TextContentElement
            const elementId = textEl.id
                ? getBaseElementId(textEl.id) ?? textEl.id
                : undefined
            const authoredUserId = userInfo()?.currentUserInfo?.kpUserId ?? ''
            const textElBase = buildTextElBase(
                textEl as TextContentElement & { authoredUserId?: string }
            )

            // Captured at postSave time; used in postGet merge to avoid
            // a stale store read at GET callback time.
            let capturedUOMElements: Partial<TextContentElement>[] | undefined
            let capturedParagraphNodeId: string | undefined

            // Fires after addProjectContentItem while the store still holds the
            // optimistic paragraph (client element IDs intact for lookup).
            const postSave = () => {
                if (!elementId) return
                const {
                    contentItemViewNodes: currentNodes,
                    actions,
                } = useWorkspace2Store.getState()
                const paragraphNode = getContentItemViewNodeByElementId(
                    currentNodes,
                    elementId
                )
                capturedParagraphNodeId = paragraphNode?.id

                const buildResult = buildNodesWithInsertedUOM({
                    currentNodes,
                    paragraphNode,
                    elementId,
                    textElBase: textElBase as Record<string, unknown>,
                    offset: cleanOffset,
                    annotated: cleanAnnotated,
                    newUOMId,
                    authoredUserId,
                })

                if (!buildResult) return

                const { newParagraphElements } = buildResult

                // Capture only the replacement slice (before-text + UOM + after-text),
                // not the entire paragraph element array. Appending the whole array in
                // postGet produced duplicate element IDs (server already has the saved
                // text element under elementId; the replacement slice starts with it too),
                // which crashed React in the new workspace.
                capturedUOMElements = newParagraphElements as Partial<TextContentElement>[]

                setUnsavedData('')
                setReactiveWorkspaceActions({
                    isInsertUOM: false,
                    omWorkspaceDirectInsertUOMHandler: undefined,
                })
                actions.setTextContentElementToAdd({ id: newUOMId })
            }

            // Fires before updateTreeViewNodes overwrites the store with server data.
            // Splices the UOM elements captured at postSave time into the server paragraph.
            const postGet = (
                serverNodes: Partial<ContentItemViewNodesType>[],
                baseNodes: Partial<ContentItemViewNodesType>[]
            ): Partial<ContentItemViewNodesType>[] => {
                if (!capturedUOMElements) return serverNodes

                // For a new (unsaved) paragraph, the client ID in capturedParagraphNodeId
                // won't match any server-assigned ID. Find the saved paragraph by looking
                // for the node present in serverNodes but absent from baseNodes.
                const baseNodeIds = new Set(
                    baseNodes.map((n) => n.id).filter(Boolean)
                )
                const targetNode = serverNodes.find(
                    (n) =>
                        n.id === capturedParagraphNodeId ||
                        (n.id && !baseNodeIds.has(n.id))
                )
                if (!targetNode) return serverNodes

                return serverNodes.map((n) => {
                    if (n !== targetNode) return n
                    const serverElements =
                        (n.elements as Partial<TextContentElement>[]) ?? []
                    const uomIdx = serverElements.findIndex(
                        (el) => el.id === newUOMId
                    )
                    if (uomIdx !== -1) return n
                    const origIdx = serverElements.findIndex(
                        (el) => el.id === elementId
                    )
                    const merged =
                        origIdx !== -1
                            ? [
                                  ...serverElements.slice(0, origIdx),
                                  ...capturedUOMElements!,
                                  ...serverElements.slice(origIdx + 1),
                              ]
                            : serverElements.length === 1
                            ? // New paragraph: server assigned a new element ID so elementId
                              // is not found. Replace the single server element entirely —
                              // appending would duplicate the text.
                              [...capturedUOMElements!]
                            : [...serverElements, ...capturedUOMElements!]
                    return {
                        ...n,
                        elements: merged,
                    }
                }) as Partial<ContentItemViewNodesType>[]
            }

            scheduleInsertAfterSave(postSave, postGet, newUOMId)

            handleAddContentExecuteOptimisticWithFallback(
                currentContent,
                textElement as TextContentElement
            )
            return
        }
        insertUOMAtCursorRef.current()
    }, [
        editorRef,
        debouncedOnChange,
        skipNextBlurRef,
        deactivateCKEditor,
        scheduleInsertAfterSave,
        handleAddContentExecuteOptimisticWithFallback,
        textElement,
        processEditorContent,
        setUnsavedData,
    ])

    // Keep the ref in sync so toolbar click handlers always call the latest version.
    useLayoutEffect(() => {
        onInsertUOMRef.current = canEnableInsertDirectUOM
            ? insertUOMAtCursorWithSaveFirst
            : undefined
    }, [
        canEnableInsertDirectUOM,
        insertUOMAtCursorWithSaveFirst,
        onInsertUOMRef,
    ])

    // Publish the UOM handler to the reactive workspace actions store so the toolbar
    // can trigger an insert. Clears itself on unmount or when the toggle turns off.
    useEffect(() => {
        if (!canEnableInsertDirectUOM || !ckeToggleActive) return
        setReactiveWorkspaceActions({
            isInsertUOM: true,
            omWorkspaceDirectInsertUOMHandler: insertUOMAtCursorWithSaveFirst,
        })
        return () => {
            const current = reactiveWorkspaceActions()
            if (current.omWorkspaceDirectInsertUOMHandler === insertUOMAtCursorWithSaveFirst) {
                setReactiveWorkspaceActions({
                    // Keep enabled if pill insertion already registered its UOM handler.
                    isInsertUOM: !!current.omWorkspaceInsertUOMHandler,
                    omWorkspaceDirectInsertUOMHandler: undefined,
                })
            }
        }
    }, [
        ckeToggleActive,
        canEnableInsertDirectUOM,
        insertUOMAtCursorWithSaveFirst,
    ])
}
